import {AfterViewInit, ChangeDetectionStrategy, Component, effect, ElementRef, HostListener, inject, input, InputSignal, OnDestroy, Renderer2, Signal, viewChild} from '@angular/core';
import {PanelLocationEnum} from '../../enums/panel-location.enum';
import {IconComponent} from '../icon/icon.component';
import {ICONS} from '../../constants/icons/icons.constants';
import {AuthQuery} from '../../queries/auth.query';
import {AiChatService} from '../../services/ai/ai-chat.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DialogService} from '../../services/dialog/dialog.service';
import {SnackbarService} from '../../services/snackbar/snackbar.service';
import {DocumentService} from '../../services/document/document.service';
import {AuthImagePipe} from '../../pipes/auth-image.pipe';
import {AiChatMessageTypeEnum} from '../../enums/ai-chat/ai-chat-message-type.enum';
import {AiChat} from '../../models/ai/ai-chat';
import {FloatingActionButtonComponent} from '../floating-action-button/floating-action-button.component';
import {ActionItemGroupComponent} from '../action-item-group/action-item-group.component';
import {ActionItemComponent} from '../action-item/action-item.component';
import {SystemInfoService} from '../../services/system-info/system-info.service';
import {AiChatContextEnum} from '../../enums/ai-chat/ai-chat-context.enum';
import {IconService} from '../../services/icon/icon.service';
import {HeaderComponent} from '../header/header.component';
import {TabletDocumentPreviewResizeObserverService} from '../../services/tablet-document-preview-resize-observer/tablet-document-preview-resize-observer.service';
import {ButtonComponent} from '../buttons/button/button.component';
import {AppButtonTypeEnum} from '../../enums/button/app-button-type.enum';
import {ButtonColorEnum} from '../../enums/button/button-color.enum';
import {AiServicesNotEnabledError, NoReadableDocumentsError, NotAuthorizedError} from '../../errors/api-errors';
import {AppIconSizeEnum} from '../../enums/app-icon-size.enum';
import {PanelHostDirective} from '../../directives/panel-host.directive';
import {PanelService} from '../../services/panel/panel.service';
import {PromptSuggestionsPanelComponent} from '../panel/prompt-suggestions-panel/prompt-suggestions-panel.component';

@Component({
    host: {
        '[class.is-tablet-size]': 'this.isTabletSize()',
        '[class.section]': 'true'
    },
    selector: 'app-ai-chat',
    imports: [
        IconComponent,
        TranslateModule,
        FloatingActionButtonComponent,
        ActionItemGroupComponent,
        ActionItemComponent,
        HeaderComponent,
        ButtonComponent,
        AuthImagePipe,
        PanelHostDirective,
    ],
    providers: [
        AuthImagePipe,
        TabletDocumentPreviewResizeObserverService
    ],
    templateUrl: './ai-chat.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './ai-chat.component.css'
})
export class AiChatComponent implements OnDestroy, AfterViewInit {
    public isTabletSize: InputSignal<boolean> = input.required<boolean>();
    protected userPrompt: Signal<ElementRef<HTMLTextAreaElement> | undefined> = viewChild<ElementRef<HTMLTextAreaElement>>('userPrompt');
    protected scrollContainer: Signal<ElementRef | undefined> = viewChild<ElementRef>('scrollContainer');
    protected actionButton: Signal<FloatingActionButtonComponent | undefined> = viewChild<FloatingActionButtonComponent | undefined>(FloatingActionButtonComponent);
    protected aiChat: Signal<AiChat>;
    protected readonly ICONS: typeof ICONS;
    protected readonly AiChatMessageTypeEnum = AiChatMessageTypeEnum;
    protected readonly PanelLocationEnum = PanelLocationEnum;
    protected userId: string;
    protected readonly AiChatContextEnum = AiChatContextEnum;
    protected hideHistoryButtonInTabletView: Signal<boolean>;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected readonly ButtonColorEnum = ButtonColorEnum;
    protected readonly IconService = IconService;
    protected readonly AppIconSizeEnum = AppIconSizeEnum;
    private observer: MutationObserver | undefined;
    private readonly panelService = inject(PanelService);
    private readonly aiChatService = inject(AiChatService);
    private readonly authQuery = inject(AuthQuery);
    private readonly renderer = inject(Renderer2);
    private readonly translateService = inject(TranslateService);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly documentService = inject(DocumentService);
    private readonly systemInfoService = inject(SystemInfoService);
    private readonly tabletDocumentPreviewResizeObserverService = inject(TabletDocumentPreviewResizeObserverService);

    public constructor() {
        this.aiChat = this.aiChatService.getAiChatSignal();
        this.hideHistoryButtonInTabletView = this.tabletDocumentPreviewResizeObserverService.getHideHistoryButtonSignal();
        this.ICONS = ICONS;
        this.userId = this.authQuery.getUserId();

        effect(() => {
            if (this.aiChat().isGeneratingMessage) {
                this.resetUserPrompt();
            }
        });
    }

    public ngAfterViewInit(): void {
        this.setupMutationObserver();
    }

    public ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    // This is necessary for the hasUnsavedChangesRouteGuard to also trigger on browser-close or tab-close.
    @HostListener('window:beforeunload', ['$event'])
    protected onBeforeUnload($event: BeforeUnloadEvent): void {
        if (this.aiChatService.getIsShowingAiChat()) {
            $event.preventDefault();
        }
    }

    protected async removeChatOverlay(): Promise<void> {
        await this.aiChatService.hideAiChatOverlay();
    }

    protected async shareMessage(): Promise<void> {
        try {
            const result = await this.aiChatService.shareLastAiMessage();
            if (this.systemInfoService.isCapacitor()) {
                if (result && result.activityType?.toLowerCase() !== 'canceled') {
                    this.snackbarService.showSuccess('AI_CHAT.SHARED_LAST_MESSAGE');
                }
            } else {
                this.snackbarService.showSuccess('AI_CHAT.COPIED_LAST_MESSAGE_TO_CLIPBOARD');
            }
        } catch (e) {
            if (this.systemInfoService.isCapacitor()) {
                this.snackbarService.showError('ERROR_SHARING', e as Error);
            } else {
                this.snackbarService.showError('ERROR_COPY_TO_CLIPBOARD', e as Error);
            }
        }
    }

    protected async compareDocuments(): Promise<void> {
        const prompt = this.translateService.instant('AI_CHAT.PROMPTS.COMPARE');
        await this.submitChatMessage(prompt);
    }

    protected async summarizeDocuments(): Promise<void> {
        const prompt = this.translateService.instant('AI_CHAT.PROMPTS.SUMMARIZE');
        await this.submitChatMessage(prompt);
    }

    protected async onKeyDown($event: KeyboardEvent): Promise<void> {
        if ($event.key !== 'Enter' || $event.shiftKey) {
            return;
        }

        $event.preventDefault();

        const userPrompt = this.userPrompt()?.nativeElement.value;
        if (userPrompt) {
            await this.submitChatMessage(userPrompt.trim());
        }
    }

    // This is needed to show a confirm dialog before opening a link that will navigate to a document
    protected async handleDocumentLinkClick(event: Event): Promise<void> {
        const target = event.target as HTMLElement;

        const linkToOpen = target.getAttribute('href');
        if (target.tagName === 'A' && linkToOpen) {
            event.stopPropagation();
            event.preventDefault();

            await this.hideActionMenuAndPanels();
            const confirmDialogText = this.translateService.instant('AI_CHAT.CONFIRM_OPEN_LINK', { filename: target.textContent });
            if (await this.dialogService.showConfirmDialog({
                messageTranslationKey: confirmDialogText
            })) {
                await this.aiChatService.hideAiChatOverlay(true);
                await this.documentService.openDocumentViaDeepLink(linkToOpen);
            }
        }
    }

    protected async submitChatMessage(prompt: string): Promise<void> {
        await this.aiChatService.submitChatMessage(prompt);
        await this.hideActionMenuAndPanels();
    }

    protected async clearChat(): Promise<void> {
        const noSubmittedPrompts = this.aiChat()
            .chatMessages
            .filter(message => message().messageType === AiChatMessageTypeEnum.userPrompt).length === 0;

        if (noSubmittedPrompts) {
            return;
        }

        try {
            await this.aiChatService.clearChat();
        } catch (error) {
            await this.aiChatService.hideAiChatOverlay(true);

            if (error instanceof NotAuthorizedError) {
                this.snackbarService.showError('AI_CHAT.START_CONVERSATION.MISSING_PERMISSION_ERROR', error);
            } else if (error instanceof AiServicesNotEnabledError) {
                this.snackbarService.showError('AI_CHAT.START_CONVERSATION.VAULT_AI_SERVICES_DISABLED_ERROR', error);
            } else if (error instanceof NoReadableDocumentsError) {
                this.snackbarService.showError('AI_CHAT.START_CONVERSATION.NO_READABLE_DOCUMENTS_ERROR', error);
            } else {
                this.snackbarService.showError('AI_CHAT.START_CONVERSATION.UNKNOWN_ERROR', error as Error);
            }
        }
    }

    protected openPromptSuggestionsPanel(): void {
        this.panelService.openPanel(PanelLocationEnum.Content, PromptSuggestionsPanelComponent);
    }

    private resetUserPrompt(): void {
        const userPromptElement: HTMLTextAreaElement | undefined = this.userPrompt()?.nativeElement as HTMLTextAreaElement;
        if (userPromptElement) {
            this.renderer.setProperty(userPromptElement, 'value', '');
            userPromptElement.blur();
        }
    }

    // This exists for scrolling down when a new message is inserted/retrieved
    private setupMutationObserver(): void {
        this.observer = new MutationObserver(() => {
            const scrollContainer = this.scrollContainer();
            if (scrollContainer?.nativeElement) {
                this.renderer.setProperty(scrollContainer.nativeElement, 'scrollTop', scrollContainer.nativeElement.scrollHeight);
            }
        });

        const scrollContainer = this.scrollContainer();
        if (scrollContainer?.nativeElement) {
            this.observer.observe(scrollContainer.nativeElement, {
                childList: true,
                subtree: true
            });
        }
    }

    private async hideActionMenuAndPanels(): Promise<void> {
        const actionButton = this.actionButton();
        if (actionButton) {
            await actionButton.closeMenu();
        }

        this.panelService.closeAll();
    }
}
